/**
 * API 配置文件
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Message } from '@arco-design/web-react';
import {
  evaluateAuthRecovery,
  type AuthRecoveryDecision,
} from './auth-recovery';
import { isJwtTokenFresh } from '@/utils/jwt';
import { createLogger } from '@/utils/logger';

// API 基础 URL
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

// 创建 axios 实例
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const REDIRECT_COOLDOWN_MS = 5000;
const MAX_CONSECUTIVE_401_BEFORE_REDIRECT = 2;
const ACCESS_TOKEN_CACHE_TTL_MS = 60 * 1000;

let lastRedirectTime = 0;
let consecutive401Failures = 0;
let refreshSessionPromise: Promise<string | null> | null = null;
let cachedAccessToken: string | null = null;
let cachedAccessTokenExpireAt = 0;

let totalResponseCount = 0;
let total401Count = 0;
let total5xxCount = 0;
let totalRetrySuccessCount = 0;

const logger = createLogger('ApiConfig');

interface AuthMetrics {
  reason: string;
  totalResponseCount: number;
  total401Count: number;
  total5xxCount: number;
  auth401Ratio: number;
  totalRetrySuccessCount: number;
  retrySuccessRate: number;
  updatedAt: number;
}

declare global {
  interface Window {
    __AUTH_METRICS__?: AuthMetrics;
  }
}

function reportAuthMetrics(reason: string): void {
  const auth401Ratio = totalResponseCount > 0
    ? Number((total401Count / totalResponseCount).toFixed(4))
    : 0;
  const retrySuccessRate = total401Count > 0
    ? Number((totalRetrySuccessCount / total401Count).toFixed(4))
    : 0;

  if (typeof window !== 'undefined') {
    window.__AUTH_METRICS__ = {
      reason,
      totalResponseCount,
      total401Count,
      total5xxCount,
      auth401Ratio,
      totalRetrySuccessCount,
      retrySuccessRate,
      updatedAt: Date.now(),
    };
  }
}

interface RetryableAxiosRequestConfig extends AxiosRequestConfig {
  __retryCount?: number;
  __startTime?: number;
  __url?: string;
}

type AdapterConfigLike = AxiosRequestConfig & {
  signal?: AbortSignal;
  params?: Record<string, unknown>;
  data?: unknown;
};

interface ErrorWithConfig extends Error {
  config?: AxiosRequestConfig;
  response?: {
    status: number;
    statusText: string;
    data?: {
      error?: string;
      [key: string]: unknown;
    };
    headers?: Record<string, string> | Headers;
  };
  request?: unknown;
  code?: string;
}

function headersToRecord(headers: Headers): Record<string, string> {
  const result: Record<string, string> = {};
  headers.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const err = error as ErrorWithConfig;
    return err.message || fallback;
  }
  return fallback;
}

function getResponseDataErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === 'object' && data !== null) {
    const responseData = data as { error?: string };
    return responseData.error || fallback;
  }
  return fallback;
}

function getCurrentReturnUrl(): string {
  if (typeof window === 'undefined') {
    return '/';
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function buildRequestUrl(config?: AxiosRequestConfig): string {
  if (!config) {
    return '';
  }

  const baseURL = config.baseURL || API_BASE_URL;
  const url = config.url || '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `${baseURL}${url}`;
}

function readTokenFromStorage(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const token = localStorage.getItem('token');
  if (!isJwtTokenFresh(token)) {
    if (token) {
      localStorage.removeItem('token');
    }
    return null;
  }

  return token;
}

function writeTokenToStorage(token: string | null): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (token) {
    localStorage.setItem('token', token);
    return;
  }
  localStorage.removeItem('token');
}

function setCachedAccessToken(token: string | null): void {
  cachedAccessToken = token;
  cachedAccessTokenExpireAt = token
    ? Date.now() + ACCESS_TOKEN_CACHE_TTL_MS
    : 0;
}

async function resolveAccessToken(): Promise<string | null> {
  const localToken = readTokenFromStorage();
  if (localToken) {
    setCachedAccessToken(localToken);
    return localToken;
  }

  if (
    cachedAccessToken &&
    cachedAccessTokenExpireAt > Date.now() &&
    isJwtTokenFresh(cachedAccessToken)
  ) {
    return cachedAccessToken;
  }
  return null;
}

async function refreshAccessTokenOnce(): Promise<string | null> {
  if (!refreshSessionPromise) {
    refreshSessionPromise = (async () => {
      try {
        const { supabase } = await import('@/lib/supabase/client');
        const {
          data: { session },
          error,
        } = await supabase.auth.refreshSession();

        if (error || !session?.access_token) {
          setCachedAccessToken(null);
          return null;
        }

        writeTokenToStorage(session.access_token);
        setCachedAccessToken(session.access_token);
        return session.access_token;
      } catch (_error) {
        setCachedAccessToken(null);
        return null;
      } finally {
        refreshSessionPromise = null;
      }
    })();
  }

  return refreshSessionPromise;
}

// 请求拦截器
apiClient.interceptors.request.use(
  async (config) => {
    const requestConfig = config as RetryableAxiosRequestConfig;
    // 性能监控：记录请求开始时间
    if (process.env.NODE_ENV === 'development') {
      const url = requestConfig.url || '';
      const fullUrl = requestConfig.baseURL ? `${requestConfig.baseURL}${url}` : url;
      requestConfig.__startTime = performance.now();
      requestConfig.__url = fullUrl;
    }

    const token = await resolveAccessToken();
    if (token) {
      requestConfig.headers = requestConfig.headers || {};
      (requestConfig.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      if (process.env.NODE_ENV === 'development' && requestConfig.url?.includes('/user/permissions')) {
        logger.debug('[API Config] 使用缓存 token');
      }
    } else if (process.env.NODE_ENV === 'development' && requestConfig.url?.includes('/user/permissions')) {
      logger.debug('[API Config] 没有找到 token');
    }

    // 对于项目相关的 API，使用 fetch adapter 绕过 mockjs
    const url = requestConfig.url || '';
    const fullUrl = requestConfig.baseURL ? `${requestConfig.baseURL}${url}` : url;

    if (
      fullUrl.includes('/api/projects') ||
      fullUrl.includes('/api/frameworks') ||
      fullUrl.includes('/api/users') ||
      fullUrl.includes('/api/user/permissions') ||
      fullUrl.includes('/api/departments') ||
      fullUrl.includes('/api/roles') ||
      fullUrl.includes('/api/integrations') ||
      fullUrl.includes('/api/cost-standards') ||
      fullUrl.includes('/api/employee-levels') ||
      fullUrl.includes('/api/city-types') ||
      fullUrl.includes('/api/client-departments') ||
      fullUrl.includes('/api/suppliers') ||
      fullUrl.includes('/api/stage-templates') ||
      fullUrl.includes('/api/presales') ||
      fullUrl.includes('/api/contributions') ||
      fullUrl.includes('/api/approval-process-templates') ||
      fullUrl.includes('/api/performance-records') ||
      fullUrl.includes('/api/costs') ||
      fullUrl.includes('/api/approval-center') ||
      fullUrl.includes('/api/approvals') ||
      fullUrl.includes('/api/notifications') ||
      fullUrl.includes('/api/task-types') ||
      fullUrl.includes('/api/workbench') ||
      fullUrl.includes('/api/search') ||
      fullUrl.includes('/api/hrbp')
    ) {
      // 使用 fetch adapter 绕过 mockjs 的 XMLHttpRequest 拦截
      requestConfig.adapter = async (adapterConfig) => {
        const normalizedAdapterConfig = (adapterConfig || {}) as AdapterConfigLike;
        // 使用外部传入的 signal，如果没有则创建新的
        const externalSignal = normalizedAdapterConfig.signal || requestConfig.signal;
        const controller = externalSignal 
          ? null 
          : new AbortController();
        const signal = externalSignal || controller?.signal;
        
        let adapterUrl = normalizedAdapterConfig.url || '';
        
        // 处理查询参数
        // 如果 URL 中已经包含查询参数，先分离 URL 和查询字符串
        const urlParts = adapterUrl.split('?');
        const baseUrl = urlParts[0];
        const existingQuery = urlParts[1] || '';
        const params = existingQuery ? new URLSearchParams(existingQuery) : new URLSearchParams();
        
        // 合并 adapterConfig.params 中的参数
        if (normalizedAdapterConfig.params) {
          Object.keys(normalizedAdapterConfig.params).forEach((key) => {
            const value = normalizedAdapterConfig.params?.[key];
            if (value !== undefined && value !== null) {
              if (Array.isArray(value)) {
                // 数组参数：转换为逗号分隔的字符串（后端期望的格式）
                params.set(key, value.join(','));
              } else {
                params.set(key, String(value));
              }
            }
          });
        }
        
        // 重新构建 URL
        const queryString = params.toString();
        if (queryString) {
          adapterUrl = baseUrl + '?' + queryString;
        } else {
          adapterUrl = baseUrl;
        }
        
        const adapterFullUrl = normalizedAdapterConfig.baseURL
          ? `${normalizedAdapterConfig.baseURL}${adapterUrl}`
          : adapterUrl;

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (normalizedAdapterConfig.headers) {
          Object.keys(normalizedAdapterConfig.headers).forEach((key) => {
            const value = normalizedAdapterConfig.headers?.[key];
            if (
              value !== undefined &&
              value !== null &&
              key.toLowerCase() !== 'content-type'
            ) {
              headers[key] = String(value);
            }
          });
        }

        // 确保 Authorization header 被正确传递
        if (requestConfig.headers && 'Authorization' in requestConfig.headers) {
          headers['Authorization'] = String(
            (requestConfig.headers as Record<string, unknown>).Authorization || ''
          );
        }

        if (process.env.NODE_ENV === 'development' && adapterFullUrl.includes('/user/permissions')) {
          logger.debug('[Fetch Adapter] Authorization header', `${headers['Authorization']?.substring(0, 50) || ''}...`);
        }

        // 获取请求数据
        // axios 的 adapter 中，POST/PUT 等请求的数据在 config.data 中
        // 但 adapterConfig 是 config 的副本，所以数据应该在 adapterConfig.data 中
        // 如果 adapterConfig.data 不存在，尝试从原始 config.data 获取
        let requestData =
          normalizedAdapterConfig.data !== undefined
            ? normalizedAdapterConfig.data
            : requestConfig.data !== undefined
            ? requestConfig.data
            : null;

        // 如果 requestData 已经是字符串（可能被双重序列化了），尝试解析
        if (typeof requestData === 'string') {
          try {
            requestData = JSON.parse(requestData);
          } catch (_parseRequestError) {
            // 如果解析失败，保持原样
            logger.warn('Fetch adapter: requestData 是字符串但无法解析为 JSON', requestData);
          }
        }

        // 调试：打印发送的数据（已注释，避免控制台日志过多）
        // if (process.env.NODE_ENV === 'development') {
        //   console.log(
        //     'Fetch adapter - adapterConfig.data:',
        //     adapterConfig.data
        //   );
        //   console.log('Fetch adapter - config.data:', config.data);
        //   console.log('Fetch adapter - 最终使用的数据:', requestData);
        //   if (requestData) {
        //     console.log(
        //       'Fetch adapter 发送的数据 (JSON):',
        //       JSON.stringify(requestData, null, 2)
        //     );
        //   }
        // }

        // 使用配置的超时时间，如果没有则使用默认值
        const timeout = normalizedAdapterConfig.timeout || requestConfig.timeout || 30000;
        let timeoutId: NodeJS.Timeout | null = null;
        
        // 只有在没有外部 signal 时才设置超时
        if (!externalSignal && controller) {
          timeoutId = setTimeout(
            () => controller.abort(),
            timeout
          );
        }

        try {
          // 确保 body 是正确的 JSON 字符串
          const bodyString = requestData
            ? JSON.stringify(requestData)
            : undefined;

          if (process.env.NODE_ENV === 'development' && bodyString) {
            logger.debug('Fetch adapter - 实际发送的 body 字符串', bodyString);
          }

          let response: Response;
          try {
            response = await fetch(adapterFullUrl, {
              method: (
                adapterConfig.method ||
                requestConfig.method ||
                'GET'
              ).toUpperCase(),
              headers,
              body: bodyString,
              signal: signal,
              credentials: 'include', // 包含 credentials 以传递 Authorization header
            });
          } catch (fetchError: unknown) {
            const typedFetchError = fetchError as ErrorWithConfig;
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            // 处理 fetch 失败的情况（网络错误、CORS 错误等）
            if (typedFetchError.name === 'AbortError' || typedFetchError.name === 'AbortController') {
              throw typedFetchError;
            }
            // 将 fetch 错误转换为更友好的错误信息
            const errorMessage = getErrorMessage(typedFetchError, String(fetchError));
            if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
              throw new Error(
                `无法连接到后端服务器 (${adapterFullUrl})，请确保后端服务正在运行`
              );
            }
            throw new Error(errorMessage || '网络请求失败');
          }

          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          // 检查响应状态
          if (!response.ok) {
            let errorData: { error?: string } | Record<string, unknown> | null = null;
            let errorMessage = `请求失败: ${response.status} ${response.statusText}`;

            try {
              const errorText = await response.text();
              if (errorText) {
                try {
                  errorData = JSON.parse(errorText);
                } catch (_parseError) {
                  errorData = { error: errorText };
                }
              }
            } catch (_readError) {
              // 读取失败时继续使用默认错误信息
            }

            if (errorData && typeof errorData === 'object' && 'error' in errorData) {
              const typedErrorData = errorData as { error?: string };
              errorMessage = typedErrorData.error || errorMessage;
            }

            const responseError = new Error(errorMessage) as ErrorWithConfig;
            responseError.response = {
              status: response.status,
              statusText: response.statusText,
              data: errorData || { error: errorMessage },
              headers: headersToRecord(response.headers),
            };
            responseError.config = normalizedAdapterConfig as AxiosRequestConfig;
            throw responseError;
          }

          const data = await response.json();

          return {
            data,
            status: response.status,
            statusText: response.statusText,
            headers: headersToRecord(response.headers),
            config: normalizedAdapterConfig as AxiosRequestConfig,
          };
        } catch (error: unknown) {
          const typedError = error as ErrorWithConfig;
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (typedError.name === 'AbortError') {
            // 如果是外部 signal 触发的取消，直接抛出原始错误
            if (externalSignal && externalSignal.aborted) {
              throw typedError;
            }
            throw new Error('请求超时');
          }
          throw typedError;
        }
      };
    }

    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // 性能监控：记录请求耗时
    if (process.env.NODE_ENV === 'development') {
      const config = response.config as RetryableAxiosRequestConfig;
      if (config.__startTime) {
        const duration = performance.now() - config.__startTime;
        const url = config.__url || config.url || '';
        const method = config.method?.toUpperCase() || 'GET';

        // 打印慢请求警告（超过 3 秒）- 阈值提高以减少日志
        if (duration > 3000) {
          logger.warn(
            `⚠️ 慢请求: ${method} ${url} 耗时 ${Math.round(duration)}ms`
          );
        }
        // 注释掉普通请求的日志，避免控制台过多输出
        // else if (duration > 500) {
        //   console.info(
        //     `ℹ️ 请求: ${method} ${url} 耗时 ${Math.round(duration)}ms`
        //   );
        // }
      }
    }

    totalResponseCount += 1;
    consecutive401Failures = 0;

    const { data } = response;

    // 调试：打印响应数据
    if (
      process.env.NODE_ENV === 'development' &&
      response.config.url?.includes('/cost-standards')
    ) {
      logger.debug('响应拦截器 - 原始 response.data', data);
      logger.debug('响应拦截器 - response.status', response.status);
    }

    // 如果后端返回的是 { success: true, data: ... } 格式
    if (
      data &&
      typeof data === 'object' &&
      'success' in data &&
      data.success === false
    ) {
      const errorMessage = getResponseDataErrorMessage(data, '请求失败');
      Message.error(errorMessage);
      return Promise.reject(new Error(errorMessage));
    }
    // 返回 data，这样在 API 函数中可以直接使用 response.data, response.total 等
    return data;
  },
  async (error) => {
    const typedError = error as ErrorWithConfig;
    totalResponseCount += 1;

    // 性能监控：记录错误请求耗时
    if (process.env.NODE_ENV === 'development') {
      const config = typedError.config as RetryableAxiosRequestConfig | undefined;
      if (config?.__startTime) {
        const duration = performance.now() - config.__startTime;
        const url = config.__url || config.url || '';
        const method = config.method?.toUpperCase() || 'GET';
        logger.error(
          `❌ 请求失败: ${method} ${url} 耗时 ${Math.round(duration)}ms`,
          typedError.message
        );
      }
    }

    // 处理错误响应
    if (typedError.response) {
      const { status, data } = typedError.response;
      const requestConfig = (typedError.config || {}) as RetryableAxiosRequestConfig;
      const requestUrl = buildRequestUrl(requestConfig);

      switch (status) {
        case 401: {
          total401Count += 1;
          const pathname = typeof window !== "undefined" ? window.location.pathname : "";
          const now = Date.now();

          let decision: AuthRecoveryDecision = evaluateAuthRecovery({
            status,
            retryCount: requestConfig.__retryCount || 0,
            pathname,
            requestUrl,
            consecutive401Failures,
            now,
            lastRedirectTime,
            maxConsecutiveFailures: MAX_CONSECUTIVE_401_BEFORE_REDIRECT,
            redirectCooldownMs: REDIRECT_COOLDOWN_MS,
          });

          if (decision.action === "ignore") {
            break;
          }

          if (decision.action === "retry") {
            requestConfig.__retryCount = 1;

            const refreshedToken = await refreshAccessTokenOnce();
            if (refreshedToken) {
              requestConfig.headers = requestConfig.headers || {};
              (requestConfig.headers as Record<string, string>).Authorization = `Bearer ${refreshedToken}`;
              consecutive401Failures = 0;
              totalRetrySuccessCount += 1;
              reportAuthMetrics("retry_success");
              return apiClient.request(requestConfig);
            }

            decision = evaluateAuthRecovery({
              status,
              retryCount: requestConfig.__retryCount || 1,
              pathname,
              requestUrl,
              consecutive401Failures,
              now: Date.now(),
              lastRedirectTime,
              maxConsecutiveFailures: MAX_CONSECUTIVE_401_BEFORE_REDIRECT,
              redirectCooldownMs: REDIRECT_COOLDOWN_MS,
            });
          }

          consecutive401Failures = decision.nextConsecutive401Failures;
          lastRedirectTime = decision.nextLastRedirectTime;
          reportAuthMetrics(decision.action === "redirect" ? "redirect_to_login" : "401_received");
          Message.error("登录状态可能已过期，请重新登录");

          if (decision.action === "redirect" && typeof window !== "undefined") {
            const returnUrl = encodeURIComponent(getCurrentReturnUrl());
            window.location.href = `/login?returnUrl=${returnUrl}`;
          }


          break;
        }
        case 403:
          Message.error('没有权限');
          break;
        case 404:
          Message.error('请求的资源不存在');
          break;
        case 500:
          total5xxCount += 1;
          reportAuthMetrics('500_received');
          Message.error(data?.error || '服务器错误');
          break;
        default:
          Message.error(data?.error || '请求失败');
      }
    } else if (typedError.request) {
      Message.error('网络错误，请检查网络连接');
    } else {
      // 检查是否是取消的请求
      const errorMessage = typedError.message || String(typedError);
      const isCanceled =
        errorMessage.toLowerCase().includes('cancel') ||
        errorMessage.toLowerCase().includes('abort') ||
        typedError.name === 'CanceledError' ||
        typedError.name === 'AbortError';

      if (isCanceled) {
        // 取消的请求不需要显示错误，这是预期的行为
        return Promise.reject(typedError);
      }

      // 请求配置错误，输出更详细的错误信息用于调试
      logger.error('请求配置错误', typedError);
      logger.error('错误详情', {
        message: typedError.message,
        config: typedError.config,
        stack: typedError.stack,
      });
      Message.error(`请求配置错误: ${errorMessage}`);
    }
    return Promise.reject(typedError);
  }
);

export default apiClient;
